import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import NotificationDropdown from 'components/navbar/top/NotificationDropdown';
import ProfileDropdown from 'components/navbar/top/ProfileDropdown';
import AppContext from 'context/Context';
import React, { useContext, useState } from 'react';
import {
  Button,
  Nav,
  OverlayTrigger,
  Tooltip,
  Dropdown,
  Badge,
  Spinner
} from 'react-bootstrap';
import AuthContext, { useAuthContext } from 'context/authContext';
import CreateReminderModal from 'components/dashboards/CreateReminderModal';
import SoftBadge from 'components/common/SoftBadge';
import {
  faSignOutAlt,
  faCheck,
  faTrash,
  faPlus,
  faEdit
} from '@fortawesome/free-solid-svg-icons';
import {
  useMyReminders,
  useCompleteReminder,
  useDeleteReminder
} from 'hooks/useReminders';
import { usePendingRemindersCount } from 'hooks/useDashboard';
import usePinnedMobileMenu from 'hooks/usePinnedMobileMenu';
import dayjs from 'dayjs';
import paths from 'routes/paths';
import { Link } from 'react-router-dom';

const REMINDER_TYPE_COLORS = {
  follow_up: 'info',
  payment: 'success',
  meeting: 'primary',
  task: 'secondary',
  update: 'warning'
};

const REMINDER_TYPE_LABELS = {
  follow_up: 'Follow Up',
  payment: 'Payment',
  meeting: 'Meeting',
  task: 'Task',
  update: 'Update'
};

const TopNavRightSideNavItem = () => {
  const {
    config: { isDark, isRTL },
    setConfig
  } = useContext(AppContext);
  const { logout } = useContext(AuthContext);
  const { user } = useAuthContext();
  const [showReminderModal, setShowReminderModal] = useState(false);
  const [editingReminder, setEditingReminder] = useState(null);

  const { reminders, isLoading: remindersLoading } = useMyReminders();
  const { count: remindersCount } = usePendingRemindersCount();
  const { completeReminder, isCompleting } = useCompleteReminder();
  const { deleteReminder, isDeleting } = useDeleteReminder();
  const pinnedMenu = usePinnedMobileMenu();

  const handleComplete = (e, id) => {
    e.stopPropagation();
    completeReminder(id);
  };

  const handleDelete = (e, id) => {
    e.stopPropagation();
    if (window.confirm('Delete this reminder?')) {
      deleteReminder(id);
    }
  };

  const handleEdit = (e, reminder) => {
    e.stopPropagation();
    setEditingReminder(reminder);
    setShowReminderModal(true);
  };

  const handleCloseModal = () => {
    setShowReminderModal(false);
    setEditingReminder(null);
  };

  return (
    <Nav
      navbar
      className="navbar-nav-icons flex-row align-items-center"
      as="ul"
    >
      {/* System notification alerts */}
      <NotificationDropdown />

      {/* Reminders Dropdown */}
      <Nav.Item as={'li'} className="me-2">
        <Dropdown align="end" onToggle={pinnedMenu.onToggle}>
          <Dropdown.Toggle
            as={Button}
            variant="falcon-default"
            size="sm"
            ref={pinnedMenu.toggleRef}
            title="Reminders"
          >
            <FontAwesomeIcon icon="clock" className="me-md-1" />
            {/* Icon only on phones so the top bar fits */}
            <span className="d-none d-md-inline">Reminders</span>
            <Badge
              bg={remindersCount > 0 ? 'danger' : 'secondary'}
              pill
              className="ms-1 ms-md-2"
            >
              {remindersCount}
            </Badge>
          </Dropdown.Toggle>
          <Dropdown.Menu
            className="py-0"
            style={{
              minWidth: 'min(420px, 92vw)',
              maxHeight: '500px',
              overflowY: 'auto',
              ...pinnedMenu.menuStyle
            }}
          >
            <div className="bg-light px-3 py-2 d-flex justify-content-between align-items-center border-bottom">
              <span className="fw-bold fs-0">My Reminders</span>
              <Button
                variant="link"
                size="sm"
                className="p-0 text-decoration-none"
                onClick={() => setShowReminderModal(true)}
              >
                <FontAwesomeIcon icon={faPlus} className="me-1" />
                Add
              </Button>
            </div>
            {remindersLoading ? (
              <div className="text-center py-4">
                <Spinner animation="border" size="sm" />
              </div>
            ) : reminders.length === 0 ? (
              <div className="text-center py-4 text-muted fs-0">
                No pending reminders
              </div>
            ) : (
              reminders.map(reminder => {
                const isOverdue = dayjs(reminder.due_date).isBefore(
                  dayjs(),
                  'day'
                );
                const isToday = dayjs(reminder.due_date).isSame(dayjs(), 'day');
                const assignedByOther =
                  reminder.created_by_user_id !== user?.id &&
                  reminder.created_by;
                const isCreatedBySelf =
                  reminder.created_by_user_id === user?.id;
                return (
                  <Dropdown.Item
                    key={reminder.id}
                    className="px-3 py-3 border-bottom"
                    as="div"
                  >
                    <div className="d-flex justify-content-between align-items-start">
                      <div className="flex-1 me-2">
                        <div className="fw-semi-bold text-800 mb-1 fs-0 text-wrap">
                          {reminder.title}
                        </div>
                        {reminder.description && (
                          <p
                            className="mb-2 text-600 fs--1"
                            style={{ whiteSpace: 'normal', lineHeight: '1.5' }}
                          >
                            {reminder.description}
                          </p>
                        )}
                        <div className="d-flex align-items-center gap-2 flex-wrap">
                          <SoftBadge
                            bg={REMINDER_TYPE_COLORS[reminder.reminder_type]}
                            pill
                            className="fs--1"
                          >
                            {REMINDER_TYPE_LABELS[reminder.reminder_type]}
                          </SoftBadge>
                          <span
                            className={
                              isOverdue
                                ? 'text-danger fw-medium fs--1'
                                : isToday
                                ? 'text-warning fw-medium fs--1'
                                : 'text-500 fs--1'
                            }
                          >
                            {dayjs(reminder.due_date).format('DD MMM YYYY')}
                            {reminder.due_time &&
                              ` ${String(reminder.due_time).slice(0, 5)}`}
                          </span>
                          {assignedByOther && (
                            <span className="text-info fs--1">
                              by {reminder.created_by.first_name}{' '}
                              {reminder.created_by.last_name}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="d-flex gap-1 flex-shrink-0">
                        {isCreatedBySelf && (
                          <>
                            <Button
                              variant="falcon-primary"
                              size="sm"
                              className="px-2 py-1"
                              onClick={e => handleEdit(e, reminder)}
                              title="Edit"
                            >
                              <FontAwesomeIcon
                                icon={faEdit}
                                className="fs--1"
                              />
                            </Button>
                            <Button
                              variant="falcon-danger"
                              size="sm"
                              className="px-2 py-1"
                              onClick={e => handleDelete(e, reminder.id)}
                              disabled={isDeleting}
                              title="Delete"
                            >
                              <FontAwesomeIcon
                                icon={faTrash}
                                className="fs--1"
                              />
                            </Button>
                          </>
                        )}
                        <Button
                          variant="falcon-success"
                          size="sm"
                          className="px-2 py-1"
                          onClick={e => handleComplete(e, reminder.id)}
                          disabled={isCompleting}
                          title="Complete"
                        >
                          <FontAwesomeIcon icon={faCheck} className="fs--1" />
                        </Button>
                      </div>
                    </div>
                  </Dropdown.Item>
                );
              })
            )}
            <div className="bg-light px-3 py-2 text-center border-top position-sticky bottom-0">
              <Link
                to={paths.reminders}
                className="fw-semi-bold fs--1 text-decoration-none"
              >
                View and manage all reminders
              </Link>
            </div>
          </Dropdown.Menu>
        </Dropdown>
      </Nav.Item>

      {/* Add Reminder Button */}
      <Nav.Item as={'li'} className="me-2">
        <Button
          variant="falcon-primary"
          size="sm"
          onClick={() => setShowReminderModal(true)}
          title="Add Reminder"
        >
          <FontAwesomeIcon icon={faPlus} className="me-md-1" />
          <span className="d-none d-md-inline">Add Reminder</span>
        </Button>
      </Nav.Item>

      <Nav.Item as={'li'}>
        <Nav.Link
          className="px-2 theme-control-toggle"
          onClick={() => setConfig('isDark', !isDark)}
        >
          <OverlayTrigger
            key="left"
            placement={isRTL ? 'bottom' : 'left'}
            overlay={
              <Tooltip id="ThemeColor">
                {isDark ? 'Switch to light theme' : 'Switch to dark theme'}
              </Tooltip>
            }
          >
            <div className="theme-control-toggle-label">
              <FontAwesomeIcon
                icon={isDark ? 'sun' : 'moon'}
                className="fs-0"
              />
            </div>
          </OverlayTrigger>
        </Nav.Link>
      </Nav.Item>

      <ProfileDropdown />

      <Nav.Item as={'li'}>
        <Nav.Link className="px-2" onClick={logout}>
          <OverlayTrigger
            key="logout"
            placement={isRTL ? 'bottom' : 'left'}
            overlay={<Tooltip id="LogoutTooltip">Sign out</Tooltip>}
          >
            <div>
              <FontAwesomeIcon
                icon={faSignOutAlt}
                className="fs-0 text-danger"
              />
            </div>
          </OverlayTrigger>
        </Nav.Link>
      </Nav.Item>

      {/* Reminder Modal */}
      <CreateReminderModal
        show={showReminderModal}
        onHide={handleCloseModal}
        editingReminder={editingReminder}
      />
    </Nav>
  );
};

export default TopNavRightSideNavItem;
